/**
 * Singleton modal controller.
 */
import { getConfig, getLabels } from '../config.js';
import { createState, ModalState } from './state.js';
import { trapFocus } from './focus-trap.js';
import {
	renderLoading,
	renderPackChoices,
	renderUnsupported,
	renderUnavailable,
	setLiveMessage,
} from './renderer.js';
import { qs } from '../utils/dom.js';
import { refreshConvertedPrices } from '../integrations/kestrel-currency.js';

export function createModal() {
	const state = createState();
	let root = null;
	let dialog = null;
	let body = null;
	let content = null;
	let live = null;
	let previouslyFocused = null;

	function ensure() {
		root = document.getElementById('wcqb-modal');
		if (!root) {
			root = document.createElement('div');
			root.id = 'wcqb-modal';
			root.className = 'wcqb-modal';
			root.hidden = true;
			root.setAttribute('aria-hidden', 'true');
			root.innerHTML = `
				<div class="wcqb-modal__backdrop" data-wcqb-close="backdrop"></div>
				<div class="wcqb-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="wcqb-modal-title" tabindex="-1">
					<button type="button" class="wcqb-modal__close" data-wcqb-close="button" aria-label="Close"><span aria-hidden="true">&times;</span></button>
					<div class="wcqb-modal__body" data-wcqb-body>
						<div class="wcqb-modal__content" data-wcqb-content></div>
					</div>
					<div class="wcqb-modal__live" data-wcqb-live aria-live="polite" aria-atomic="true"></div>
				</div>
			`;
			document.body.appendChild(root);
		}
		dialog = qs('.wcqb-modal__dialog', root);
		body = qs('[data-wcqb-body]', root);
		content = qs('[data-wcqb-content]', root);
		live = qs('[data-wcqb-live]', root);
		return root;
	}

	function lockScroll(lock) {
		document.documentElement.classList.toggle('wcqb-modal-open', lock);
		document.body.classList.toggle('wcqb-modal-open', lock);
	}

	/**
	 * Open the singleton modal.
	 *
	 * @param {number} productId
	 * @param {string} context
	 * @param {Element|null} trigger
	 * @param {object|null} preloadedOptions When provided, skip the loading skeleton
	 *   and render these options immediately (used when packs are already fetched).
	 */
	function open(productId, context, trigger, preloadedOptions = null) {
		ensure();
		previouslyFocused = trigger || document.activeElement;
		state.state = ModalState.OPENING;
		state.productId = productId;
		state.context = context || 'archive';
		state.trigger = trigger || null;
		state.options = null;
		state.selectedVariationId = null;
		state.errorMessage = '';
		state.successMessage = '';
		state.adding = false;

		root.hidden = false;
		root.setAttribute('aria-hidden', 'false');
		root.classList.add('is-open');
		lockScroll(true);

		if (preloadedOptions) {
			showOptions(preloadedOptions);
		} else {
			showLoading();
		}

		if (dialog) {
			dialog.focus();
		}
	}

	function close(force = false) {
		if (!force && state.state === ModalState.ADDING) {
			return;
		}
		ensure();
		state.state = ModalState.CLOSED;
		state.productId = null;
		state.options = null;
		state.selectedVariationId = null;
		root.classList.remove('is-open');
		root.hidden = true;
		root.setAttribute('aria-hidden', 'true');
		lockScroll(false);
		setLiveMessage(live, '');
		if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
			previouslyFocused.focus();
		}
		previouslyFocused = null;
	}

	function showLoading() {
		state.state = ModalState.LOADING;
		if (content) {
			renderLoading(content);
			content.hidden = false;
		}
	}

	function showOptions(options) {
		if (!content) {
			return;
		}

		state.options = options;
		state.errorMessage = '';
		content.hidden = false;

		// Hide the unused PHP loading placeholder if present.
		const loadingPlaceholder = qs('[data-wcqb-loading]', root);
		if (loadingPlaceholder) {
			loadingPlaceholder.hidden = true;
		}

		if (options.mode === 'unsupported') {
			state.state = ModalState.UNSUPPORTED;
			renderUnsupported(content, options);
			refreshConvertedPrices();
			return;
		}

		if (options.mode === 'unavailable' || !(options.variations || []).some((v) => v.purchasable)) {
			state.state = ModalState.UNAVAILABLE;
			renderUnavailable(content, options);
			refreshConvertedPrices();
			return;
		}

		const defaultId = options.default_variation_id;
		const defaultOk =
			defaultId &&
			(options.variations || []).some(
				(v) => Number(v.variation_id) === Number(defaultId) && v.purchasable
			);
		state.selectedVariationId = defaultOk ? Number(defaultId) : null;
		state.state = state.selectedVariationId ? ModalState.LOADED_SELECTED : ModalState.LOADED;
		renderPackChoices(content, options, state.selectedVariationId);
		refreshConvertedPrices();
	}

	function selectVariation(variationId) {
		if (!state.options) {
			return;
		}
		const variation = (state.options.variations || []).find(
			(v) => Number(v.variation_id) === Number(variationId) && v.purchasable
		);
		if (!variation) {
			return;
		}
		state.selectedVariationId = Number(variationId);
		state.state = ModalState.LOADED_SELECTED;
		renderPackChoices(content, state.options, state.selectedVariationId);
		refreshConvertedPrices();
	}

	function setAdding(isAdding) {
		state.adding = isAdding;
		state.state = isAdding ? ModalState.ADDING : state.selectedVariationId ? ModalState.LOADED_SELECTED : ModalState.LOADED;
		const addBtn = content && content.querySelector('[data-action="add-to-basket"]');
		const labels = getLabels();
		if (addBtn) {
			addBtn.disabled = isAdding || !state.selectedVariationId;
			addBtn.textContent = isAdding ? labels.adding || 'Adding…' : labels.addToBasket || 'Add to basket';
		}
	}

	function showError(message) {
		state.state = ModalState.ERROR;
		state.errorMessage = message;
		state.adding = false;
		setLiveMessage(live, message);
		const addBtn = content && content.querySelector('[data-action="add-to-basket"]');
		const labels = getLabels();
		if (addBtn) {
			addBtn.disabled = !state.selectedVariationId;
			addBtn.textContent = labels.addToBasket || 'Add to basket';
		}
	}

	function showSuccess(message) {
		state.state = ModalState.SUCCESS;
		state.successMessage = message;
		state.adding = false;
		setLiveMessage(live, message);
		const config = getConfig();
		if (config.closeAfterAdd) {
			window.setTimeout(() => close(true), 700);
		}
	}

	function onKeydown(event) {
		if (!root || root.hidden) {
			return;
		}
		if (event.key === 'Escape') {
			if (state.state !== ModalState.ADDING) {
				close();
			}
			return;
		}
		if (dialog) {
			trapFocus(dialog, event);
		}
	}

	function getSelectedVariation() {
		if (!state.options || !state.selectedVariationId) {
			return null;
		}
		return (state.options.variations || []).find(
			(v) => Number(v.variation_id) === Number(state.selectedVariationId)
		);
	}

	ensure();

	return {
		ensure,
		open,
		close,
		showLoading,
		showOptions,
		selectVariation,
		setAdding,
		showError,
		showSuccess,
		onKeydown,
		getSelectedVariation,
		getState: () => state,
		getRoot: () => root,
	};
}
